import { call, put, takeEvery } from 'redux-saga/effects'
import { Endpoints } from 'src/constants/endpoints'
import { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi, ApiService } from '../api/callApi'
import { FundingInvoice, FundingSettings, Payment, PaymentsSummary } from 'src/interfaces/payment'
import {
  CancelFundingInvoice,
  DownloadFundingInvoice,
  FetchFundingInvoice,
  FetchFundingSettings,
  FetchPaymentsSummary,
  ResetFundingInvoice,
  SubmitFundingInvoiceRequest,
  SubmitFundingMethodRequest,
} from 'src/constants/actionTypes'
import { FundingType } from 'src/constants/payment/enums'

function* onFetchFundingSettings(action: AsyncAction<null, FundingSettings>) {
  try {
    const { payload }: { payload: FundingSettings } = yield call(
      callSecureApi,
      Endpoints.FetchFundingSettings,
      {
        apiService: ApiService.Paraworks,
      },
    )

    yield put(FetchFundingSettings.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch fund methods'

    yield put(FetchFundingSettings.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface FetchFundingInvoiceResponse {
  currentInvoice: FundingInvoice | null
}

function* onFetchFundingInvoice(action: AsyncAction<null, FetchFundingInvoiceResponse>) {
  try {
    const { payload }: { payload: FetchFundingInvoiceResponse } = yield call(
      callSecureApi,
      Endpoints.FetchFundingInvoice,
      {
        apiService: ApiService.Paraworks,
      },
    )

    yield put(FetchFundingInvoice.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch funding invoice'

    yield put(FetchFundingInvoice.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface CancelFundingInvoiceRequest {
  invoiceId: string
}

function* onCancelFundingInvoice(action: AsyncAction<CancelFundingInvoiceRequest>) {
  const { invoiceId } = action.payload

  try {
    const {
      payload,
    }: {
      payload: {
        id: string
      }
    } = yield call(callSecureApi, Endpoints.CancelFundingInvoice, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      params: [
        {
          value: invoiceId,
          field: ':composedInvoiceId',
        },
      ],
    })

    yield put(CancelFundingInvoice.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to cancel funding invoice'
    yield put(CancelFundingInvoice.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface SubmitFundingInvoiceRequest {
  invoiceId: string
}

function* onSubmitFundingInvoiceRequest(action: AsyncAction<SubmitFundingInvoiceRequest>) {
  const { invoiceId } = action.payload

  try {
    const {
      payload,
    }: {
      payload: {
        id: string
      }
    } = yield call(callSecureApi, Endpoints.SubmitFundingInvoiceRequest, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      params: [
        {
          value: invoiceId,
          field: ':composedInvoiceId',
        },
      ],
    })

    yield put(SubmitFundingInvoiceRequest.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to submit funding invoice request'
    yield put(SubmitFundingInvoiceRequest.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface DownloadFundingInvoiceRequest {
  invoiceId: string
}

function* onDownloadFundingInvoice(
  action: AsyncAction<DownloadFundingInvoiceRequest, Array<Payment>>,
) {
  const { invoiceId } = action.payload

  try {
    const { payload }: { payload: Array<Payment> } = yield call(
      callSecureApi,
      Endpoints.DownloadFundingInvoice,
      {
        apiService: ApiService.Paraworks,
        params: [
          {
            value: invoiceId,
            field: ':composedInvoiceId',
          },
        ],
        returnBuffer: true,
      },
    )

    yield put(DownloadFundingInvoice.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to download invoice'

    yield put(DownloadFundingInvoice.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface SubmitFundingMethodRequest {
  type: FundingType
}

function* onSubmitFundingMethodRequest(action: AsyncAction<SubmitFundingMethodRequest>) {
  const { type } = action.payload

  try {
    const {
      payload,
    }: {
      payload: {
        id: string
      }
    } = yield call(callSecureApi, Endpoints.SubmitFundingMethodRequest, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      params: [
        {
          value: type,
          field: ':fundingType',
        },
      ],
    })

    yield put(SubmitFundingMethodRequest.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to submit funding invoice method request'
    yield put(SubmitFundingMethodRequest.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface ResetFundingInvoiceRequest {
  invoiceId: string
}

function* onResetFundingInvoice(action: AsyncAction<ResetFundingInvoiceRequest>) {
  const { invoiceId } = action.payload

  try {
    const {
      payload,
    }: {
      payload: {
        id: string
      }
    } = yield call(callSecureApi, Endpoints.ResetFundingInvoice, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      params: [
        {
          value: invoiceId,
          field: ':composedInvoiceId',
        },
      ],
    })

    yield put(ResetFundingInvoice.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to reset funding invoice'
    yield put(ResetFundingInvoice.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface FetchPaymentsSummaryResponse {
  paymentsSummary: PaymentsSummary
}

function* onFetchPaymentsSummary(action: AsyncAction<null, FetchPaymentsSummaryResponse>) {
  try {
    const { payload }: { payload: FetchPaymentsSummaryResponse } = yield call(
      callSecureApi,
      Endpoints.FetchPaymentsSummary,
      {
        apiService: ApiService.Paraworks,
      },
    )

    yield put(FetchPaymentsSummary.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch settlements summary'

    yield put(FetchPaymentsSummary.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* fundSaga() {
  yield takeEvery(FetchFundingSettings.type.REQUEST, onFetchFundingSettings)
  yield takeEvery(FetchFundingInvoice.type.REQUEST, onFetchFundingInvoice)
  yield takeEvery(CancelFundingInvoice.type.REQUEST, onCancelFundingInvoice)
  yield takeEvery(SubmitFundingInvoiceRequest.type.REQUEST, onSubmitFundingInvoiceRequest)
  yield takeEvery(DownloadFundingInvoice.type.REQUEST, onDownloadFundingInvoice)
  yield takeEvery(SubmitFundingMethodRequest.type.REQUEST, onSubmitFundingMethodRequest)
  yield takeEvery(ResetFundingInvoice.type.REQUEST, onResetFundingInvoice)
  yield takeEvery(FetchPaymentsSummary.type.REQUEST, onFetchPaymentsSummary)
}
